import React from "react";
import PropTypes from "prop-types";
import "./HeatmapGrid.scss";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * HeatmapGrid — weekday × hour of day. Answers "when do people come?", which a daily
 * trend cannot: the week's rhythm, lunchtime peaks, the evening scroll.
 *
 * `markers` (same shape) draws a dot in any cell where something that matters happened —
 * quotes submitted, say — so the rarer event can be read against the traffic.
 */
const HeatmapGrid = ({ matrix, markers, color, unit, markerLabel }) => {
  const max = Math.max(1, ...matrix.flat());
  const rowTotals = matrix.map((r) => r.reduce((a, b) => a + b, 0));
  const colTotals = Array.from({ length: 24 }, (_, h) => matrix.reduce((t, r) => t + r[h], 0));
  const peakHour = colTotals.indexOf(Math.max(...colTotals));
  const peakDay = rowTotals.indexOf(Math.max(...rowTotals));
  return (
    <div className="heatmap" style={color ? { "--heat-color": color } : undefined}>
      <div className="heatmap__grid" role="table" aria-label="Activity by weekday and hour">
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="heatmap__hour">{h % 3 === 0 ? `${String(h).padStart(2, "0")}` : ""}</span>
        ))}
        <span className="heatmap__total-head">Total</span>
        {matrix.map((row, d) => (
          <React.Fragment key={DAYS[d]}>
            <span className={`heatmap__day${d === peakDay ? " heatmap__day--peak" : ""}`}>{DAYS[d]}</span>
            {row.map((v, h) => {
              const m = markers ? markers[d][h] : 0;
              return (
                <span
                  key={h}
                  className="heatmap__cell"
                  style={{ "--heat": v / max }}
                  title={`${DAYS[d]} ${String(h).padStart(2, "0")}:00–${String(h + 1).padStart(2, "0")}:00 — ${v.toLocaleString("en-GB")} ${unit || ""}${m ? ` · ${m} ${markerLabel || ""}` : ""}`}
                >
                  {m > 0 && <i className="heatmap__marker" />}
                </span>
              );
            })}
            <span className="heatmap__total">{rowTotals[d].toLocaleString("en-GB")}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="heatmap__foot">
        <span>Busiest: <strong>{DAYS[peakDay]}</strong>, and <strong>{String(peakHour).padStart(2, "0")}:00–{String(peakHour + 1).padStart(2, "0")}:00</strong> across the week</span>
        <span className="heatmap__scale">less <i /> more{markers && <><i className="heatmap__marker heatmap__marker--legend" /> {markerLabel}</>}</span>
      </div>
    </div>
  );
};

HeatmapGrid.propTypes = {
  matrix: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  markers: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  color: PropTypes.string,
  unit: PropTypes.string,
  markerLabel: PropTypes.string,
};

export default HeatmapGrid;
